import * as React from 'react';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import {
  Employee,
  EmployeeToBusiness,
  LoadedUser,
  ModalData,
} from '../interfaces';
import SideBarModal from '../components/modal/SideBarModal';
import { removeEmployeeFromShop } from '../services/business';
import { createRow, headerCells } from '../utils/table/teamTable';
import ReuseableTable from '../components/table/ReuseableTable';
import FullPageModal from '../components/modal/FullPageModal';
import NewEditArtistForm from '../components/form/NewEditArtistForm';
import OnboardedModal from '../components/modal/OnboardedModal';
import useUser from '../hooks/global/useUser';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import useConfirm from '../hooks/useConfirm';
import PageLayout from '../components/global/PageLayout';
import { createFooterAction } from '../helpers/global';
import { isUserAdminOrManager } from '../utils/global/helper';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import useSubscriptionUpsell from '../hooks/subscriptions/useSubscriptionUpsell';

const EMPTY_MESSAGE = 'No employees added yet!';

export default function TeamPage() {
  const confirm = useConfirm();
  const { user: loadedUser, triggerReload } = useUser<LoadedUser>();

  const [modalData, setModalData] = React.useState<ModalData>();
  const [staffModalOpen, setStaffModalOpen] = React.useState(false);

  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee>();
  const [staffAdded, setStaffAdded] = React.useState(false);

  const { openSubscriptionModal } = useSubscriptionModal();
  const isIndependent = loadedUser?.business.type === 'INDEPENDENT';
  const canManageTeam = useFeatureGuard(Feature.MANAGE_TEAM);
  const { requireUpsell, upsell } = useSubscriptionUpsell(Feature.MANAGE_TEAM);

  // Set up click action for each row of the employees table
  const openEmployee = React.useCallback(
    (newModalData: ModalData, employee: Employee) => {
      if (requireUpsell) {
        upsell();
        return;
      }

      if (!canManageTeam) {
        openSubscriptionModal();
        return;
      }

      setSelectedEmployee(employee);
      setModalData(newModalData);
    },
    [canManageTeam, openSubscriptionModal, requireUpsell, upsell],
  );

  const closeAllModals = () => {
    setSelectedEmployee(undefined);
    setStaffModalOpen(false);
  };

  const onDeleteEmployee = React.useCallback(async () => {
    if (!selectedEmployee || !loadedUser || !canManageTeam) return;

    const confirmed = await confirm({
      title: 'Delete User',
      message: `If you delete ${selectedEmployee?.displayName}'s account, they will no longer be able to access their account. Any appointments scheduled with ${selectedEmployee?.displayName} should be canceled, or changed to a different artist before you deactivate their account.`,
      positiveText: 'Delete',
    });
    if (!confirmed) return;

    try {
      await removeEmployeeFromShop(selectedEmployee, loadedUser.business.id);
      triggerReload();
      closeAllModals();
      toast.success('Employee Successfully Removed', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    } catch {
      toast.error('Error removing employee', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    }
  }, [canManageTeam, confirm, loadedUser, selectedEmployee, triggerReload]);

  const openNewStaff = () => {
    if (requireUpsell) {
      upsell();
      return;
    }

    if (!canManageTeam) {
      openSubscriptionModal();
      return;
    }
    setStaffModalOpen(true);
  };

  // Create table rows
  const employeeRows = loadedUser?.business.employees.map(employee =>
    createRow({
      currentUser: loadedUser,
      employee,
      handleClick: openEmployee,
    }),
  );

  const footerActions = React.useMemo(() => {
    const actions = [
      createFooterAction(
        'Edit',
        canManageTeam ? () => setStaffModalOpen(true) : openSubscriptionModal,
      ),
      createFooterAction(
        'Delete',
        canManageTeam ? onDeleteEmployee : openSubscriptionModal,
      ),
    ];
    return actions;
  }, [canManageTeam, onDeleteEmployee, openSubscriptionModal]);

  const employeeToBusiness = React.useMemo(() => {
    if (!selectedEmployee) return null;
    const employeeToBus = (loadedUser?.business.employees.find(
      e => e.employeeId === selectedEmployee.employeeId,
    ) ??
      selectedEmployee.employeesToBusinesses?.find(
        eb => eb.businessId === loadedUser?.business.id,
      )) as EmployeeToBusiness;
    return employeeToBus ?? null;
  }, [
    loadedUser?.business.employees,
    loadedUser?.business.id,
    selectedEmployee,
  ]);

  const onCloseStaffModal = async () => {
    if (selectedEmployee) {
      const confirmed = await confirm({
        title: 'Exit without saving?',
        message:
          'Are you sure you want to exit without saving? Any changes you made will be lost.',
        positiveText: 'Exit',
      });
      if (!confirmed) return;
    }

    setStaffModalOpen(false);
  };

  return (
    <>
      <PageLayout
        title="Team"
        guard={{
          isAllowed:
            isIndependent ||
            (loadedUser?.canManageTeam && isUserAdminOrManager(loadedUser)),
        }}
        topSection={<OnboardingBanner step="STAFF_ONBOARD" />}>
        <div className="header-right">
          <Button
            variant="contained"
            className="primary"
            onClick={openNewStaff}>
            Add New
          </Button>
        </div>

        <ReuseableTable
          name="All Users"
          rows={employeeRows || []}
          headerCells={headerCells}
          emptyMessage={EMPTY_MESSAGE}
          loading={false}
        />

        {modalData && selectedEmployee && (
          <SideBarModal
            modalOpen={!!selectedEmployee}
            setModalOpen={() => setSelectedEmployee(undefined)}
            name={modalData.name}
            sections={modalData.sections}
            footerActions={footerActions}
          />
        )}
      </PageLayout>
      <FullPageModal
        modalOpen={staffModalOpen}
        setModalOpen={onCloseStaffModal}
        name={selectedEmployee ? 'Edit user' : 'Add a user'}>
        <NewEditArtistForm
          setModalOpen={setStaffModalOpen}
          onSuccess={triggerReload}
          setStaffAdded={setStaffAdded}
          setSelectedEmployee={setSelectedEmployee}
          employee={selectedEmployee}
          employeeToBusiness={employeeToBusiness}
        />
      </FullPageModal>

      <OnboardedModal
        open={staffAdded}
        setOpen={setStaffAdded}
        bookingLink={`web.getporter.io/artist/${selectedEmployee?.urlPath}`}
        firstName={selectedEmployee?.user?.firstName || 'Your new user'}
        phoneNumber={selectedEmployee?.user?.phoneNumber || ''}
      />
    </>
  );
}
